'use client'

import useCartProduct from '@/app/[locale]/_hooks/useCartProduct'
import { ADD_TO_CART } from '@/app/[locale]/_store/cart/cart.slice'
import { IModifierQuantityObj, IRedux } from '@/app/[locale]/_store/types'
import {
  IProduct,
  IProductVariant,
  IProperty,
  ISavedModifier,
} from '@/app/[locale]/_types'
import { toaster } from '@/components/ui/toaster'
import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import OriginOptions from '../OriginOptions'
import Modifiers from '../Modifiers'
import Counter from '../Counter'
import { Button } from '@/components/ui/button'
import NumberToPrice from '@/app/[locale]/_components/NumberToPrice'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import { usePathname, useRouter } from 'next/navigation'
import useGTM from '@/app/[locale]/_hooks/useGTM'
import { createUrlWithParams } from '@/app/[locale]/_utils/createUrlWithParams'
import { track } from '@/app/[locale]/_utils/utm'
import useProductDiscount from '@/app/[locale]/_hooks/useProductDiscount'
import {
  getMissingRequiredModifierNames,
  validateRequiredModifiers,
} from '@/app/[locale]/_utils/modifier/validateRequiredModifiers'

// const Favourites = dynamic(() => import('../Favourites'), {
//   ssr: false,
// })

interface IVariantsMap {
  [key: string]: any
}
interface IProductOption {}

const Details = ({
  data,
  onSuccess,
  onRedirectToCart,
}: {
  data?: IProduct
  onSuccess?: () => void
  onRedirectToCart?: () => void
}) => {
  const [loading, setLoading] = useState(true)
  const [startTracking, setStartTracking] = useState(false)
  const [expectedModifier, setExpectedModifier] = useState<string[]>([])
  const [compulsories, setCompulsories] = useState<ISavedModifier[]>([])

  const [modifiersPrice, setModifiersPrice] = useState(0)
  const [originProps, setOriginProps] = useState<IProperty[]>([])
  const [modifiersQuantity, setModifiersQuantity] = useState<
    IModifierQuantityObj[]
  >([])
  const [productVariants, setProductVariants] = useState<IVariantsMap>({})
  const [orderModifiers, setOrderModifiers] = useState<ISavedModifier[]>([])
  const [activeVariant, setActiveVariant] = useState<IProductVariant | null>(
    null
  )
  const [activeOptions, setActiveOptions] = useState<IProductOption[] | null>(
    null
  )

  const t = useTranslations()
  const dispatch = useDispatch()
  const pathname = usePathname()
  const currentLocale = useCustomLocale()
  const { menu_id, is_hall } = useSelector((state: IRedux) => state.session)
  const commonState = useSelector((state: IRedux) => state.common)
  const scrollToExpectedModifierRef = useRef<HTMLDivElement>(null)
  const country = useSelector((state: IRedux) => state.settings?.country)

  const router = useRouter()
  const { sendGTM } = useGTM()
  const productId = activeVariant?.id || data?.id || ''

  const { quantity, onIncrease, onDecrease, productInCart } = useCartProduct(
    productId,
    orderModifiers
    // { remove: removeFromCartGTM }
  )

  const price: number = useMemo(
    () => activeVariant?.out_price || data?.out_price || 0,
    [activeVariant?.out_price, data?.out_price]
  )

  const { discounts } = useProductDiscount()

  const hookDiscount = useMemo(() => {
    return (
      discounts?.reduce((acc, cur) => {
        if (
          cur.discount_id === 'e46ce26c-5f1b-4ca6-89f1-c013ed5f58ce' &&
          productId !== '673c4d0a-1368-40ff-bef5-32c57dbbf964'
        ) {
          return acc
        }
        if (
          cur?.product_and_discount &&
          Object.keys(cur?.product_and_discount).length > 0
        ) {
          if (cur.product_and_discount[productId]) {
            acc += cur.product_and_discount[productId]
          }
        } else if (cur.discount_for === 'product') {
          if (cur.discount_mode === 'fixed') {
            acc -= cur.discount_amount
          } else if (cur.discount_mode === 'percent') {
            acc -= (price / 100) * cur.discount_amount
          }
        }
        return acc
      }, 0) ?? 0
    )
  }, [discounts, price, productId])

  const totalDiscount = useMemo(() => {
    if (data?.discounts?.length) {
      return data.discounts.reduce((acc, cur) => acc + cur.discount_price, 0)
    }
    return hookDiscount
  }, [data?.discounts, hookDiscount])

  const addToCartGTM = useCallback(() => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window?.dataLayer?.push({ ecommerce: null })
      sendGTM('add_to_cart', { product: data, quantity })
      window?.dataLayer?.push({ items: null, content_ids: null })
      sendGTM('add_to_cart_ads', { product: data })
      sendGTM('add_to_cart_fb', { product: data, currency: country?.currency })
    } else {
      console.warn('GTM dataLayer is not available.')
    }
  }, [data, quantity, currentLocale, pathname])

  const addToCartHandler = () => {
    if (data?.id) {
      if (data?.type == 'origin') {
        if (activeVariant) {
          dispatch(
            ADD_TO_CART({
              key: '',
              comment: '',
              in_stop: false,
              default_product: false,
              discount_percent:
                totalDiscount !== 0
                  ? Math.abs(
                      Math.round(
                        (totalDiscount / activeVariant.out_price) * 100
                      )
                    )
                  : 0,
              product_name: data.title?.[currentLocale],
              category_name: data.categories?.[0].title?.[currentLocale],
              price_with_discount: activeVariant.out_price + totalDiscount,
              price: activeVariant?.out_price,
              product_id: activeVariant?.id,
              type: 'variant',
              variants: {},
              order_modifiers: orderModifiers,
              quantity: quantity,
            })
          )
          track({
            action_type: 'add_to_cart',
          })
          sendGTM('add_to_cart', { product: activeVariant, quantity })
        } else {
          toaster.create({
            title: t('choose_one_options'),
            type: 'warning',
          })
        }
      } else {
        dispatch(
          ADD_TO_CART({
            key: '',
            comment: '',
            in_stop: false,
            default_product: false,
            discount_percent:
              totalDiscount !== 0
                ? Math.abs(Math.round((totalDiscount / price) * 100))
                : 0,
            product_name: data.title?.[currentLocale],
            category_name: data.categories?.[0].title?.[currentLocale],
            price: price,
            price_with_discount: price + totalDiscount,
            product_id: data?.id,
            type: data?.type,
            variants: {},
            order_modifiers: orderModifiers,
            quantity: quantity,
          })
        )
        track({
          action_type: 'add_to_cart',
        })
        addToCartGTM()
      }
    }
  }

  const onOrderClick = () => {
    if (checkModifierRequired()) {
      addToCartHandler()
      if (onSuccess) {
        onSuccess()
      } else {
        router.push(createUrlWithParams('/'))
      }
    } else {
      setStartTracking(true)
    }
  }
  // Get compulsory and not selected modifier
  const checkModifierRequired = () =>
    validateRequiredModifiers(compulsories, orderModifiers, modifiersQuantity)

  // Get compulsory and not selected modifier
  useEffect(() => {
    if (startTracking && compulsories?.length > 0) {
      setExpectedModifier(
        getMissingRequiredModifierNames(
          compulsories,
          orderModifiers,
          modifiersQuantity,
          currentLocale
        )
      )
    }
  }, [
    compulsories,
    orderModifiers,
    modifiersQuantity,
    startTracking,
    currentLocale,
  ])

  useEffect(() => {
    if (!expectedModifier?.length) return
    scrollToExpectedModifierRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [expectedModifier])

  const onOptionChange = (val: any, idx: any) => {
    setActiveOptions((prevState: any) =>
      prevState?.map((e: any, i: number) => (i == idx ? val : e))
    )
  }

  useEffect(() => {
    for (const [key, value] of Object.entries(productVariants)) {
      if (key == activeOptions?.join('_')) {
        setActiveVariant(value)
        break
      } else {
        setActiveVariant(null)
      }
    }
  }, [activeOptions, productVariants])
  // Calculate discounts total price

  useEffect(() => {
    if (orderModifiers) {
      let sum = 0
      for (const modifier of orderModifiers) {
        sum += modifier.modifier_price * modifier.modifier_quantity
      }
      setModifiersPrice(sum)
    }
  }, [orderModifiers])

  useEffect(() => {
    if (data && data?.type === 'origin') {
      const variantsMap: { [key: string]: any } = {}
      const optionsMap: { [key: string]: string } = {}
      const firstOptions: any[] = []
      const variantProducts = Array.isArray(data.variant_products)
        ? data.variant_products
        : []
      for (const variantProduct of variantProducts) {
        const productProperty = Array.isArray(variantProduct?.product_property)
          ? variantProduct.product_property
          : []
        let optionIds = ''
        for (let j = 0; j < productProperty.length; j++) {
          const element = productProperty[j]
          optionsMap[element.option_id] = element.option_id
          if (optionIds.length > 0) {
            optionIds += '_' + element.option_id
          } else {
            optionIds += element.option_id
          }
        }
        variantsMap[optionIds] = variantProduct
      }
      setProductVariants(variantsMap)
      setOriginProps(data?.properties)
      Object.keys(variantsMap)[0]
        ?.split('_')
        .forEach((option) => {
          firstOptions.push(option)
        })
      setActiveOptions(firstOptions)
    }
  }, [data])

  const selectedMenuId =
    is_hall === 'true'
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : commonState?.branch?.menu_id || ''

  // const { data: favourites } = useQuery({
  //   queryKey: ['product-favourites', data?.id],
  //   queryFn: () =>
  //     productService
  //       .getProductFavourites(
  //         {
  //           product_ids: data?.id,
  //           menu_id: selectedMenuId,
  //         },
  //         settings?.shipper_id
  //       )
  //       .then((res) => res.data),
  // })

  return (
    <>
      <Box mt={6} flex={1} px={{ base: 4, lg: 0 }}>
        <OriginOptions
          originProps={originProps}
          activeOptions={activeOptions}
          productVariants={productVariants}
          onOptionChange={onOptionChange}
        />
        <Modifiers
          setLoading={setLoading}
          productInCart={productInCart}
          product={activeVariant ? activeVariant : data}
          quantity={quantity}
          expectedModifiers={expectedModifier}
          onSaveCompulsories={(e: any[]) => setCompulsories(e)}
          orderModifiers={orderModifiers}
          modifiersQuantity={modifiersQuantity}
          setOrderModifiers={setOrderModifiers}
          setModifiersQuantity={setModifiersQuantity}
          scrollToExpectedModifierRef={scrollToExpectedModifierRef}
        />
        {/* 
        <Box mb={favourites?.favourites?.length ? '150px' : 0}>
          <Favourites favourites={favourites} />
        </Box> */}
      </Box>
      <Stack
        position='sticky'
        bottom={0}
        left={0}
        bg='background.card'
        zIndex={10}
        w='full'
        gap={{ base: 4, lg: 6 }}
        pt={4}
        pb={{ base: 4, lg: 2 }}
        px={{ base: 4, lg: 0 }}
        borderTop='1px solid'
        borderColor='#00000014'
        boxShadow={{
          base: '0px -4px 6px -2px rgba(0, 0, 0, 0.08)',
          lg: 'none',
        }}
      >
        <Flex
          w='full'
          justifyContent='space-between'
          fontSize='sm'
          fontWeight={600}
        >
          <Text
            fontSize={{ base: 'sm', lg: 'xl' }}
            fontWeight={500}
            flex={1}
            lineClamp={1}
          >
            {t('price')}
          </Text>
          <HStack alignItems={{ base: 'center', lg: 'center' }}>
            {totalDiscount !== 0 && (
              <Box
                textDecor={'line-through'}
                fontSize={{ base: 'xs', lg: 'sm' }}
                color={'fg.error'}
              >
                <NumberToPrice
                  textProps={{
                    lineHeight: 1,
                    fontWeight: 600,
                  }}
                  spanProps={{
                    lineHeight: 1,
                    fontWeight: 600,
                  }}
                  hideCurrency
                  value={
                    productInCart?.quantity
                      ? (price + modifiersPrice) * productInCart.quantity
                      : (price + modifiersPrice) * quantity
                  }
                />
              </Box>
            )}
            <Text as='span'>
              <NumberToPrice
                textProps={{
                  fontWeight: 600,
                  fontSize: { base: 'sm', lg: '2xl' },
                  color: 'price',
                }}
                spanProps={{
                  fontWeight: 600,
                  fontSize: { base: 'sm', lg: '2xl' },
                  color: 'price',
                }}
                value={
                  productInCart?.quantity
                    ? (price + modifiersPrice + totalDiscount) *
                      productInCart.quantity
                    : (price + modifiersPrice + totalDiscount) * quantity
                }
              />
            </Text>
          </HStack>
        </Flex>
        <Flex gap={4} w='full'>
          <Counter
            variable={quantity}
            height={{ base: '52px', lg: '56px' }}
            onIncrease={() => onIncrease()}
            onDecrease={() => onDecrease()}
            color='text'
          />
          {Boolean(productInCart) ? (
            <>
              <Button
                flex={1}
                bg='primary'
                color='primary.text'
                _hover={{
                  bg: 'primary.hover',
                }}
                rounded={16}
                fontWeight={600}
                display={{ base: 'flex', lg: 'none' }}
                fontSize='sm'
                height='52px'
                onClick={onRedirectToCart}
              >
                {t('go_to_cart')}
              </Button>
              <Button
                flex={1}
                onClick={onRedirectToCart}
                bg='primary'
                color='primary.text'
                _hover={{
                  bg: 'primary.hover',
                }}
                rounded={16}
                fontWeight={600}
                display={{ base: 'none', lg: 'flex' }}
                fontSize='xl'
                height='56px'
              >
                {t('added_to_cart')}
              </Button>
            </>
          ) : (
            <Button
              onClick={onOrderClick}
              disabled={Boolean(
                !data || is_hall === 'true' || pathname.endsWith('/menu')
                  ? menu_id !== 'null'
                    ? menu_id && !data?.active_in_menu
                    : ''
                  : commonState?.branch?.menu_id && !data?.active_in_menu
              )}
              flex={1}
              bg='primary'
              color='primary.text'
              _hover={{
                bg: 'primary.hover',
              }}
              rounded={16}
              fontWeight={600}
              fontSize={{ base: 'sm', lg: 'xl' }}
              height={{ base: '52px', lg: '56px' }}
              loading={loading}
            >
              {expectedModifier?.length > 0
                ? `${t('select_smth', {
                    item:
                      expectedModifier[0]?.length > 20
                        ? expectedModifier[0]?.slice(0, 20) + '...'
                        : expectedModifier[0],
                  })}`
                : t('add_to_cart')}
            </Button>
          )}
        </Flex>
      </Stack>
    </>
  )
}

export default Details
